import { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Chip,
  Stack,
  Collapse,
  CircularProgress,
  Tabs,
  Tab,
  Divider,
} from '@mui/material';
import {
  Download as DownloadIcon,
  Upload as UploadIcon,
  ContentPaste as PasteIcon,
  Check as CheckIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  People as PeopleIcon,
  TableChart as TableIcon,
} from '@mui/icons-material';
import { apiClient } from '../services/documentService';
import getEnvironmentConfig from '../config/environment';
import KeyPeopleManager from './KeyPeopleManager';
import EstatePlanSummary from './EstatePlanSummary';
import { getRolesForDocumentType, getRoleTypesForDocumentTypes, ROLE_DEFINITIONS } from '../config/roleDefinitions';
import { normalizeFromUI } from '../config/fiduciarySchema';
import { validateCanonicalModel } from '../config/fiduciaryValidation';
import { computeRenderVariables } from '../config/fiduciaryRender';

const CSVDataEntry = ({
  selectedTemplates,
  onDataComplete,
  onBack
}) => {
  // Entry method: 'keyPeople' (new) or 'csv' (traditional)
  const [entryMethod, setEntryMethod] = useState('keyPeople');

  // Key People state
  const [keyPeopleData, setKeyPeopleData] = useState(null);

  // CSV state
  const [csvData, setCsvData] = useState('');
  const [parsedData, setParsedData] = useState({});
  const [errors, setErrors] = useState([]);
  const [placeholders, setPlaceholders] = useState({});
  const [showPreview, setShowPreview] = useState(false);
  const [validationResults, setValidationResults] = useState({});
  const [loadingMetadata, setLoadingMetadata] = useState(false);

  // Gateway questions state
  const [gatewayAnswers, setGatewayAnswers] = useState({
    has_minor_children: '',
    successor_levels: '',  // kept for backward compat during transition
    // Age thresholds (defaults shown, attorney can adjust)
    beneficiary_cotrust_age: 25,
    beneficiary_sole_trust_age: 30,
    trust_protector_age: 30,
    pot_trust_termination_age: 22,
    // Survivorship direction (married packages only)
    survivorship_direction: 'spouse_survives',
  });
  const [gatewaySubmitted, setGatewaySubmitted] = useState(false);

  // Summary review state (shows attorney summary before generation)
  const [showSummary, setShowSummary] = useState(false);
  const [pendingData, setPendingData] = useState(null);
  const [canonicalModel, setCanonicalModel] = useState(null);
  const [renderVars, setRenderVars] = useState(null);
  const [validationResult, setValidationResult] = useState(null);

  // Detect if this is a married package
  const isMarriedPackage = useMemo(() => {
    const pkgName = selectedTemplates?.find(t => t.type === 'package')?.name || '';
    return pkgName.includes('Disclaimer') || pkgName.includes('QTIP');
  }, [selectedTemplates]);

  // Build gateway variables object for downstream use
  const buildGatewayVars = () => ({
    has_minor_children: gatewayAnswers.has_minor_children === 'yes',
    successor_levels: parseInt(gatewayAnswers.successor_levels, 10) || 3,
    beneficiary_cotrust_age: gatewayAnswers.beneficiary_cotrust_age,
    beneficiary_sole_trust_age: gatewayAnswers.beneficiary_sole_trust_age,
    trust_protector_age: gatewayAnswers.trust_protector_age,
    pot_trust_termination_age: gatewayAnswers.pot_trust_termination_age,
    survivorship_direction: gatewayAnswers.survivorship_direction,
  });

  // Get the primary document type for role detection
  const primaryDocumentType = useMemo(() => {
    if (!selectedTemplates || selectedTemplates.length === 0) return null;
    // Check if any template is a package
    const packageTemplate = selectedTemplates.find(t =>
      t.name?.toLowerCase().includes('package') || t.type === 'package'
    );
    if (packageTemplate) return packageTemplate.name;
    // Otherwise use the first template name
    return selectedTemplates[0]?.name || null;
  }, [selectedTemplates]);

  // Build array of document type names for KeyPeopleManager
  const documentTypes = useMemo(() => {
    if (!selectedTemplates || selectedTemplates.length === 0) return [];
    return selectedTemplates.map(t => t.name);
  }, [selectedTemplates]);

  // Calculate non-role placeholders (ones that won't be filled by KeyPeopleManager)
  const nonRolePlaceholders = useMemo(() => {
    if (!placeholders || Object.keys(placeholders).length === 0) return [];

    // Get all placeholder fields that are handled by roles
    const roleHandledFields = new Set();
    const availableRoleIds = getRolesForDocumentType(primaryDocumentType || '');
    for (const roleId of availableRoleIds) {
      const role = ROLE_DEFINITIONS[roleId];
      if (role) {
        role.placeholderFields.forEach(f => roleHandledFields.add(f));
        if (role.aliasFields) {
          role.aliasFields.forEach(f => roleHandledFields.add(f));
        }
      }
    }
    // Also add client as it's a special role
    roleHandledFields.add('client');

    // Filter placeholders to find ones NOT handled by roles
    return Object.entries(placeholders)
      .filter(([key]) => {
        const cleanKey = key.replace(/^\{\{|\}\}$/g, '');
        return !roleHandledFields.has(cleanKey);
      })
      .map(([key, info]) => ({
        key: key.replace(/^\{\{|\}\}$/g, ''),
        ...info,
      }));
  }, [placeholders, primaryDocumentType]);

  const fetchMetadataAndExtractPlaceholders = async () => {
    setLoadingMetadata(true);
    const allPlaceholders = {};

    // Get API configuration
    const config = getEnvironmentConfig();
    const DOC_CREATION_API_BASE = config.aws_cloud_logic_custom.find(api => api.name === 'DocCreationAPI')?.endpoint;

    // For each template, call the orchestration endpoint to get placeholders
    for (const template of selectedTemplates) {
      try {
        console.log(`Fetching placeholders for ${template.name}...`);
        console.log(`Template type: ${template.type}`);

        // Check if this is a package
        const isPackage = template.type === 'package';

        // Use the correct orchestration endpoint
        const response = await apiClient.post(
          `${DOC_CREATION_API_BASE}/doccreation/agent`,
          {
            documentType: template.name,
            userMessage: `Create a ${template.name} document`,
            sessionId: `placeholder-extract-${Date.now()}`,
            isPackage: isPackage,
            templatePath: template.path || '',
            variables: buildGatewayVars()
          },
          { timeout: 10000 }
        );


        // Extract placeholders from response
        if (response.data) {
          let placeholderData = {};

          // Parse the assistant message which contains placeholders with descriptions
          const fullMessage = response.data.message || response.data.assistantMessage || '';

          if (fullMessage) {
            console.log(`Full message for ${template.name}:`, fullMessage.substring(0, 500));

            // Parse the table between the delimiters
            const tableMatch = fullMessage.match(/---- Copy the table below.*?----\n([\s\S]*?)---- End of table ----/);

            if (tableMatch) {
              console.log(`Found table for ${template.name}`);
              const tableContent = tableMatch[1].trim();
              const lines = tableContent.split('\n');

              // Skip header line (Placeholder    Description    Value)
              for (let i = 1; i < lines.length; i++) {
                const line = lines[i].trim();
                if (line) {
                  // Split by tab character first, then fallback to multiple spaces
                  let parts = line.split('\t');
                  if (parts.length < 2) {
                    // If no tabs found, try splitting by multiple spaces
                    parts = line.split(/\s{2,}/);
                  }

                  if (parts.length >= 2) {
                    const placeholder = parts[0].trim();
                    const description = parts[1].trim();

                    // Store with {{ }} for internal consistency
                    const formattedPlaceholder = `{{${placeholder}}}`;

                    placeholderData[formattedPlaceholder] = {
                      description: description || 'No description available',
                      required: true
                    };

                  }
                }
              }
            }
          }

          // Add placeholders to allPlaceholders
          Object.entries(placeholderData).forEach(([key, info]) => {
            if (!allPlaceholders[key]) {
              allPlaceholders[key] = {
                ...info,
                requiredBy: [template.name]
              };
            } else {
              allPlaceholders[key].requiredBy.push(template.name);
            }
          });
        }
      } catch (error) {
        console.error(`Error fetching placeholders for ${template.name}:`, error);
      }
    }

    console.log('Final allPlaceholders object:', allPlaceholders);
    setPlaceholders(allPlaceholders);
    setLoadingMetadata(false);
  };

  // Handle gateway submission
  const handleGatewaySubmit = () => {
    if (!gatewayAnswers.has_minor_children) return;
    // Derive successor_levels from the max role depth if not explicitly set
    if (!gatewayAnswers.successor_levels) {
      setGatewayAnswers(prev => ({ ...prev, successor_levels: '3' }));
    }
    setGatewaySubmitted(true);
    fetchMetadataAndExtractPlaceholders();
  };

  // Generate CSV template
  const generateCSVTemplate = () => {
    // Match the backend format exactly
    const headers = ['Placeholder', 'Description', 'Value'];
    const rows = [headers.join('\t')]; // Use tab separator

    Object.entries(placeholders).forEach(([key, info]) => {
      // Strip {{ }} from placeholder names for the CSV
      const cleanKey = key.replace(/^\{\{|\}\}$/g, '');

      rows.push([
        cleanKey,
        info.description || '',
        '' // Empty for user to fill
      ].join('\t'));
    });

    return rows.join('\n');
  };

  // Download CSV template
  const handleDownloadTemplate = () => {
    const csv = generateCSVTemplate();
    const blob = new Blob([csv], { type: 'text/tab-separated-values' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'estate-planning-template.tsv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Copy CSV to clipboard
  const handleCopyToClipboard = async () => {
    const csv = generateCSVTemplate();
    try {
      await navigator.clipboard.writeText(csv);
      alert('Template copied to clipboard! You can paste it into a spreadsheet.');
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      alert('Failed to copy to clipboard. Please try downloading instead.');
    }
  };

  // Parse CSV data
  const parseCSV = (csvText) => {
    try {
      setErrors([]);
      const lines = csvText.trim().split('\n');

      // Remove any delimiter lines
      const dataLines = lines.filter(line => !line.includes('----'));

      if (dataLines.length < 2) {
        setErrors(['Data must have at least a header row and one data row']);
        return;
      }

      // Parse the header to find column indices
      const headerLine = dataLines[0];
      let headers = [];

      // Try tab-separated first
      if (headerLine.includes('\t')) {
        headers = headerLine.split('\t').map(h => h.trim());
      } else {
        // Fall back to multiple spaces
        headers = headerLine.split(/\s{2,}/).map(h => h.trim());
      }

      // Find column indices (case-insensitive)
      const placeholderIndex = headers.findIndex(h =>
        h.toLowerCase().includes('placeholder') || h.toLowerCase() === 'key'
      );
      const valueIndex = headers.findIndex(h =>
        h.toLowerCase() === 'value' || h.toLowerCase().includes('value')
      );

      if (placeholderIndex === -1 || valueIndex === -1) {
        setErrors(['Data must have "Placeholder" and "Value" columns']);
        return;
      }

      const data = {};
      const parseErrors = [];

      // Parse data rows
      for (let i = 1; i < dataLines.length; i++) {
        const line = dataLines[i].trim();
        if (!line) continue;

        let cells = [];

        // Try tab-separated first
        if (line.includes('\t')) {
          cells = line.split('\t').map(c => c.trim());
        } else {
          // Fall back to multiple spaces
          cells = line.split(/\s{2,}/).map(c => c.trim());
        }

        if (cells.length > placeholderIndex && cells.length > valueIndex) {
          const placeholder = cells[placeholderIndex];
          const value = cells[valueIndex];

          if (placeholder && value) {
            // Store with {{ }} format for internal consistency
            const cleanPlaceholder = placeholder.replace(/^\{\{|\}\}$/g, '');
            const formattedPlaceholder = `{{${cleanPlaceholder}}}`;
            data[formattedPlaceholder] = value;
          }
        }
      }

      // Validate that all required placeholders have values
      const missingPlaceholders = Object.keys(placeholders).filter(
        key => !data[key] || data[key].trim() === ''
      );

      if (missingPlaceholders.length > 0) {
        const missingNames = missingPlaceholders.map(p => p.replace(/^\{\{|\}\}$/g, ''));
        parseErrors.push(`Missing values for: ${missingNames.join(', ')}`);
      }

      setParsedData(data);
      setErrors(parseErrors);

      // Validate data
      validateData(data);

      if (parseErrors.length === 0) {
        setShowPreview(true);
      }

    } catch (error) {
      console.error('CSV parse error:', error);
      setErrors([`Parse error: ${error.message}`]);
    }
  };

  // Validate placeholder data
  const validateData = (data) => {
    const results = {};

    Object.entries(placeholders).forEach(([key, info]) => {
      const value = data[key];
      const validation = { valid: true, warnings: [] };

      if (!value || value.trim() === '') {
        validation.valid = false;
        validation.error = 'Required value is missing';
      }

      results[key] = validation;
    });

    setValidationResults(results);
  };

  // Handle paste
  const handlePaste = async () => {
    try {
      const text = await navigator.clipboard.readText();
      setCsvData(text);
      parseCSV(text);
    } catch (error) {
      console.error('Failed to read clipboard:', error);
      setErrors(['Failed to read clipboard. Please paste manually in the text area below.']);
    }
  };

  // Handle file upload
  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target.result;
        setCsvData(text);
        parseCSV(text);
      };
      reader.readAsText(file);
    }
  };

  // Complete data entry
  const handleComplete = () => {
    if (Object.keys(parsedData).length > 0 && errors.length === 0) {
      // Strip {{ }} from keys before passing to the next step
      const cleanedData = {};
      Object.entries(parsedData).forEach(([key, value]) => {
        const cleanKey = key.replace(/^\{\{|\}\}$/g, '');
        cleanedData[cleanKey] = value;
      });

      onDataComplete({
        ...cleanedData,
        ...buildGatewayVars(),
      });
    }
  };

  // Handle Key People completion - build canonical model and show summary
  const handleKeyPeopleComplete = (peopleData, structuredPeople = []) => {
    console.log('[CSVDataEntry] Key people complete, building summary');
    setKeyPeopleData(peopleData);

    // Build the merged data
    const mergedData = {
      ...peopleData,
      ...buildGatewayVars(),
    };

    // KeyPeopleManager uses camelCase address fields; normalizeFromUI reads snake_case.
    const uiPeople = structuredPeople.map(p => ({
      id: p.id,
      name: p.name,
      relationship: p.relationship,
      assignedRoles: p.assignedRoles,
      street_address: p.streetAddress,
      city_state_zip: p.cityStateZip,
      telephone: p.phone,
    }));

    // Build canonical model for attorney summary
    try {
      const pkgName = selectedTemplates?.find(t => t.type === 'package')?.name || 'Disclaimer Package';
      const model = normalizeFromUI(
        uiPeople,
        buildGatewayVars(),
        {
          client: { full_name: peopleData.client || '', county_of_residence: peopleData.client_residence_county || '' },
          spouse: peopleData.spouse ? { full_name: peopleData.spouse } : null,
          children: [],
          dates: { signing_date: peopleData.signing_date || '', rlt_date: peopleData.rlt_date || '' },
        },
        pkgName
      );
      const vars = computeRenderVariables(model);
      const validation = validateCanonicalModel(model);

      setCanonicalModel(model);
      setRenderVars(vars);
      setValidationResult(validation);
    } catch (err) {
      console.warn('[CSVDataEntry] Could not build canonical model for summary:', err);
    }

    setPendingData(mergedData);
    setShowSummary(true);
  };

  // Confirm summary and proceed to generation
  const handleSummaryConfirm = () => {
    if (pendingData) {
      onDataComplete(pendingData);
    }
  };

  // Go back from summary to edit
  const handleSummaryBack = () => {
    setShowSummary(false);
    setPendingData(null);
  };

  // Render the Key People entry method
  const renderKeyPeopleEntry = () => (
    <Box>
      <KeyPeopleManager
        documentTypes={documentTypes}
        placeholders={placeholders}
        hasMinorChildren={gatewayAnswers.has_minor_children === 'yes'}
        successorLevels={parseInt(gatewayAnswers.successor_levels, 10) || 3}
        onPlaceholdersGenerated={handleKeyPeopleComplete}
        onBack={onBack}
      />

      {/* Show remaining placeholders that need manual entry */}
      {nonRolePlaceholders.length > 0 && (
        <Paper sx={{ p: 3, mt: 3, bgcolor: 'info.50' }}>
          <Typography variant="h6" gutterBottom>
            Additional Information Needed
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            The following fields will need to be filled separately (not tied to specific people):
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {nonRolePlaceholders.map(p => (
              <Chip
                key={p.key}
                label={p.key.replace(/_/g, ' ')}
                size="small"
                variant="outlined"
                sx={{ mb: 1 }}
              />
            ))}
          </Stack>
        </Paper>
      )}
    </Box>
  );

  // Render the CSV entry method
  const renderCSVEntry = () => (
    <>
      {/* Step 1: Download Template */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Step 1: Download Template
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          This template contains all the placeholders needed for your selected documents.
          Open it in Excel or Google Sheets, fill in the &quot;Value&quot; column, then copy and paste the entire table back here.
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadTemplate}
            size="large"
          >
            Download Template
          </Button>
          <Button
            variant="outlined"
            startIcon={<PasteIcon />}
            onClick={handleCopyToClipboard}
            size="large"
          >
            Copy to Clipboard
          </Button>
        </Stack>
        <Typography variant="caption" display="block" sx={{ mt: 1 }}>
          {Object.keys(placeholders).length} placeholders to fill
        </Typography>
      </Paper>

      {/* Step 2: Upload or Paste */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Step 2: Upload Completed Data
        </Typography>

        <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            component="label"
            startIcon={<UploadIcon />}
          >
            Upload File
            <input
              type="file"
              hidden
              accept=".csv,.tsv,.txt"
              onChange={handleFileUpload}
            />
          </Button>

          <Button
            variant="outlined"
            startIcon={<PasteIcon />}
            onClick={handlePaste}
          >
            Paste from Clipboard
          </Button>
        </Stack>

        <Typography variant="body2" color="text.secondary" gutterBottom>
          Or paste your data here (copy from your spreadsheet including headers):
        </Typography>

        <TextField
          multiline
          rows={10}
          fullWidth
          value={csvData}
          onChange={(e) => {
            setCsvData(e.target.value);
            if (e.target.value) {
              parseCSV(e.target.value);
            }
          }}
          placeholder="Paste your data here...
Example:
Placeholder	Description	Value
client	Full name of the client	John Smith
county	County of residence	Los Angeles"
          sx={{ fontFamily: 'monospace' }}
        />
      </Paper>
    </>
  );

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Fill Client Data
      </Typography>

      {/* Gateway Questions Step (Step 0 - Before fetching metadata) */}
      {!gatewaySubmitted && !loadingMetadata && (
        <Paper sx={{ p: 4, mb: 3, maxWidth: 600, mx: 'auto' }}>
          <Typography variant="h6" gutterBottom>
            First, a few quick questions
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            We'll customize the data template based on your answers.
          </Typography>

          <Box sx={{ mt: 3, mb: 4 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="medium">
              Does the client have any minor children (under 18)?
            </Typography>
            <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
              <Button
                variant={gatewayAnswers.has_minor_children === 'yes' ? 'contained' : 'outlined'}
                onClick={() => setGatewayAnswers({ ...gatewayAnswers, has_minor_children: 'yes' })}
              >
                Yes
              </Button>
              <Button
                variant={gatewayAnswers.has_minor_children === 'no' ? 'contained' : 'outlined'}
                onClick={() => setGatewayAnswers({ ...gatewayAnswers, has_minor_children: 'no' })}
              >
                No
              </Button>
            </Stack>
          </Box>

          {/* Survivorship Direction (married packages only) */}
          {isMarriedPackage && (
            <Box sx={{ mt: 3, mb: 4 }}>
              <Typography variant="subtitle1" gutterBottom fontWeight="medium">
                In simultaneous death, who is deemed to survive?
              </Typography>
              <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                <Button
                  variant={gatewayAnswers.survivorship_direction === 'spouse_survives' ? 'contained' : 'outlined'}
                  onClick={() => setGatewayAnswers({ ...gatewayAnswers, survivorship_direction: 'spouse_survives' })}
                >
                  Spouse survives client
                </Button>
                <Button
                  variant={gatewayAnswers.survivorship_direction === 'client_survives' ? 'contained' : 'outlined'}
                  onClick={() => setGatewayAnswers({ ...gatewayAnswers, survivorship_direction: 'client_survives' })}
                >
                  Client survives spouse
                </Button>
              </Stack>
            </Box>
          )}

          {/* Trust Governance Ages */}
          <Box sx={{ mt: 3, mb: 4 }}>
            <Typography variant="subtitle1" gutterBottom fontWeight="medium">
              Trust governance ages
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              At what ages should a beneficiary gain control of their separate trust? Defaults are typical values.
            </Typography>
            <Stack spacing={2}>
              <Stack direction="row" spacing={2} alignItems="center">
                <TextField
                  label="Co-Trustee age"
                  type="number"
                  size="small"
                  value={gatewayAnswers.beneficiary_cotrust_age}
                  onChange={(e) => setGatewayAnswers({ ...gatewayAnswers, beneficiary_cotrust_age: parseInt(e.target.value, 10) || 25 })}
                  inputProps={{ min: 18, max: 65 }}
                  sx={{ width: 140 }}
                  helperText="Becomes co-trustee"
                />
                <TextField
                  label="Sole Trustee age"
                  type="number"
                  size="small"
                  value={gatewayAnswers.beneficiary_sole_trust_age}
                  onChange={(e) => setGatewayAnswers({ ...gatewayAnswers, beneficiary_sole_trust_age: parseInt(e.target.value, 10) || 30 })}
                  inputProps={{ min: 18, max: 65 }}
                  sx={{ width: 140 }}
                  helperText="Becomes sole trustee"
                />
                <TextField
                  label="Trust Protector age"
                  type="number"
                  size="small"
                  value={gatewayAnswers.trust_protector_age}
                  onChange={(e) => setGatewayAnswers({ ...gatewayAnswers, trust_protector_age: parseInt(e.target.value, 10) || 30 })}
                  inputProps={{ min: 18, max: 65 }}
                  sx={{ width: 140 }}
                  helperText="Becomes trust protector"
                />
              </Stack>
              {gatewayAnswers.has_minor_children === 'yes' && (
                <TextField
                  label="Pot trust termination age"
                  type="number"
                  size="small"
                  value={gatewayAnswers.pot_trust_termination_age}
                  onChange={(e) => setGatewayAnswers({ ...gatewayAnswers, pot_trust_termination_age: parseInt(e.target.value, 10) || 22 })}
                  inputProps={{ min: 18, max: 40 }}
                  sx={{ width: 220 }}
                  helperText="Age of youngest child when single trust ends"
                />
              )}
            </Stack>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button onClick={onBack} size="large">
              Back
            </Button>
            <Button
              variant="contained"
              size="large"
              onClick={handleGatewaySubmit}
              disabled={!gatewayAnswers.has_minor_children}
            >
              Continue
            </Button>
          </Box>
        </Paper>
      )}

      {/* Summary Review Step — shown after Key People entry, before generation */}
      {showSummary && (
        <Box>
          <Typography variant="h5" gutterBottom>
            Review Estate Plan
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Please review the plan summary below. This is exactly what will drive document generation.
          </Typography>

          <EstatePlanSummary
            canonicalModel={canonicalModel}
            renderVariables={renderVars}
            validationResult={validationResult}
          />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
            <Button onClick={handleSummaryBack} size="large">
              Back to Edit
            </Button>
            <Button
              variant="contained"
              size="large"
              onClick={handleSummaryConfirm}
              disabled={validationResult && !validationResult.valid}
            >
              Confirm &amp; Generate Documents
            </Button>
          </Box>
        </Box>
      )}

      {/* After gateway: render tabs and Key People immediately, CSV tab waits for placeholders */}
      {gatewaySubmitted && !showSummary && (
        <>
          {/* Selected documents reminder */}
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.100' }}>
            <Typography variant="subtitle2" gutterBottom>
              Creating documents for:
            </Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap">
              {selectedTemplates.map(template => (
                <Chip
                  key={template.id}
                  label={template.name}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Stack>
          </Paper>

          {/* Entry Method Tabs */}
          <Paper sx={{ mb: 3 }}>
            <Tabs
              value={entryMethod}
              onChange={(e, newValue) => setEntryMethod(newValue)}
              variant="fullWidth"
              sx={{ borderBottom: 1, borderColor: 'divider' }}
            >
              <Tab
                value="keyPeople"
                icon={<PeopleIcon />}
                iconPosition="start"
                label="Key People & Roles (Recommended)"
              />
              <Tab
                value="csv"
                icon={<TableIcon />}
                iconPosition="start"
                label="Spreadsheet / CSV"
              />
            </Tabs>

            <Box sx={{ p: 2, bgcolor: entryMethod === 'keyPeople' ? 'success.50' : 'grey.50' }}>
              {entryMethod === 'keyPeople' ? (
                <Typography variant="body2" color="text.secondary">
                  <strong>Easier method:</strong> Add the key people in your plan (spouse, brother, etc.)
                  and check which roles each person should fill. The system will automatically populate
                  all the placeholder fields for you.
                </Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  <strong>Traditional method:</strong> Download a template spreadsheet, fill in all values
                  manually, and paste the data back. Best for bulk imports or when you have data in a spreadsheet.
                </Typography>
              )}
            </Box>
          </Paper>

          {/* Key People tab renders immediately (roles only need document types, not API placeholders) */}
          {entryMethod === 'keyPeople' && renderKeyPeopleEntry()}

          {/* CSV tab requires placeholders from API */}
          {entryMethod === 'csv' && (
            <>
              {loadingMetadata && (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                  <CircularProgress />
                </Box>
              )}

              {!loadingMetadata && Object.keys(placeholders).length === 0 && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  No placeholders found for the selected documents. Please ensure templates are properly configured.
                </Alert>
              )}

              {!loadingMetadata && Object.keys(placeholders).length > 0 && (
                <>
                  {renderCSVEntry()}

                  {/* Errors */}
                  {errors.length > 0 && (
                    <Alert severity="error" sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" gutterBottom>
                        Please fix the following issues:
                      </Typography>
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {errors.map((error, index) => (
                          <li key={index}>{error}</li>
                        ))}
                      </ul>
                    </Alert>
                  )}

                  {/* Data Preview */}
                  {showPreview && Object.keys(parsedData).length > 0 && (
                    <Paper sx={{ p: 3, mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h6" sx={{ flexGrow: 1 }}>
                          Data Preview
                        </Typography>
                        <IconButton
                          onClick={() => setShowPreview(!showPreview)}
                          size="small"
                        >
                          {showPreview ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </Box>

                      <Collapse in={showPreview}>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Placeholder</TableCell>
                                <TableCell>Your Value</TableCell>
                                <TableCell>Status</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {Object.entries(parsedData).map(([key, value]) => {
                                const validation = validationResults[key] || {};
                                const info = placeholders[key] || {};
                                return (
                                  <TableRow key={key}>
                                    <TableCell>
                                      <Typography variant="body2" fontWeight="medium">
                                        {key.replace(/^\{\{|\}\}$/g, '')}
                                      </Typography>
                                      {info.description && (
                                        <Typography variant="caption" color="text.secondary" display="block">
                                          {info.description}
                                        </Typography>
                                      )}
                                    </TableCell>
                                    <TableCell>{value}</TableCell>
                                    <TableCell>
                                      {validation.valid ? (
                                        <Tooltip title="Valid">
                                          <CheckIcon color="success" fontSize="small" />
                                        </Tooltip>
                                      ) : (
                                        <Tooltip title={validation.error}>
                                          <WarningIcon color="error" fontSize="small" />
                                        </Tooltip>
                                      )}
                                      {validation.warnings?.length > 0 && (
                                        <Tooltip title={validation.warnings.join(', ')}>
                                          <InfoIcon color="warning" fontSize="small" />
                                        </Tooltip>
                                      )}
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Collapse>
                    </Paper>
                  )}

                  {/* Actions */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
                    <Button
                      onClick={onBack}
                      size="large"
                    >
                      Back
                    </Button>

                    <Button
                      variant="contained"
                      size="large"
                      onClick={handleComplete}
                      disabled={Object.keys(parsedData).length === 0 || errors.length > 0}
                      endIcon={<CheckIcon />}
                    >
                      Continue to AI Drafting
                    </Button>
                  </Box>
                </>
              )}
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default CSVDataEntry;